import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { DocumentRequestsApi, CommentsApi, Configuration } from '@ata-digital/client-tax-system-api-client';
import { createApiConfig, apiAxiosInstance } from '@/lib/api/config';
import { useAuthState } from '@/hooks/auth/useAuth';
import { taxBinderKeys, commentQueryOptions, commentKeys } from '@/lib/query/options';
import { createLogger } from '@/utils/logger';

const log = createLogger('useDocumentRequests');
import type {
  // DocumentRequestReviewAction, // TODO(#62): Review feature - not yet in API client
  // ReviewDocumentRequestDto, // TODO(#62): Review feature - not yet in API client
  DocumentRequestStatus,
  DocumentRequestPriority,
  UpdateDocumentRequestDto,
  CreateCommentDto,
  UpdateCommentDto,
  CreateDocumentRequestDto,
  ClientResponseDto,
  ClientResponseType,
} from '@ata-digital/client-tax-system-api-client';

// Type for document request filters
type DocumentRequestFilters = {
  binderId?: string;
  clientId?: string;
  status?: DocumentRequestStatus;
  priority?: DocumentRequestPriority;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

// Query keys for document requests
export const documentRequestKeys = {
  all: ['documentRequests'] as const,
  lists: () => [...documentRequestKeys.all, 'list'] as const,
  list: (filters?: DocumentRequestFilters) => [...documentRequestKeys.lists(), filters] as const,
  details: () => [...documentRequestKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentRequestKeys.details(), id] as const,
  awaitingReview: () => [...documentRequestKeys.all, 'awaitingReview'] as const,
  awaitingReviewList: (filters?: DocumentRequestFilters) => [...documentRequestKeys.awaitingReview(), filters] as const,
} as const;

export function useUploadDocumentRequest() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  // Create config without default Content-Type for file uploads
  const fileUploadConfig = new Configuration({
    basePath: import.meta.env.VITE_API_URL || 'http://localhost:8080',
    accessToken: authState?.token || '',
    // Don't set Content-Type - let the API client handle multipart/form-data
  });

  const documentRequestsApi = new DocumentRequestsApi(fileUploadConfig, undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (uploadData: {
      documentRequestId: string;
      files: File[];
      replaceExisting?: boolean;
      runAiDetection?: boolean;
    }) => {
      const response = await documentRequestsApi.apiDocumentRequestsIdUploadPost({
        id: uploadData.documentRequestId,
        files: uploadData.files,
        replaceExisting: uploadData.replaceExisting,
        runAiDetection: uploadData.runAiDetection,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate organizer lists to refresh document request status
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      // Invalidate all organizer details since document requests are nested
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
      // Invalidate document request specific queries
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.detail(variables.documentRequestId) });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      // Invalidate document request documents to refresh the document list
      queryClient.invalidateQueries({ queryKey: ['documents', 'request', variables.documentRequestId] });
      // Also invalidate awaiting review documents
      queryClient.invalidateQueries({ queryKey: ['documentsAwaitingReview', variables.documentRequestId] });
      // Invalidate all document lists (including organizer document lists) to refresh DocumentsList
      queryClient.invalidateQueries({ queryKey: ['documents', 'list'] });
    },
    onError: (error) => {
      log.error('Document request upload failed:', error);
    },
  });
}

/**
 * Hook for preparer to upload documents on behalf of client with automatic approval
 * This combines the upload and approval steps into a single operation
 */
export function useUploadAndApproveDocumentRequest() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();

  // Create config without default Content-Type for file uploads
  const fileUploadConfig = new Configuration({
    basePath: import.meta.env.VITE_API_URL || 'http://localhost:8080',
    accessToken: authState?.token || '',
    // Don't set Content-Type - let the API client handle multipart/form-data
  });

  const documentRequestsApi = new DocumentRequestsApi(fileUploadConfig, undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (uploadData: {
      documentRequestId: string;
      files: File[];
      replaceExisting?: boolean;
      runAiDetection?: boolean;
    }) => {
      // Step 1: Upload the files
      const uploadResponse = await documentRequestsApi.apiDocumentRequestsIdUploadPost({
        id: uploadData.documentRequestId,
        files: uploadData.files,
        replaceExisting: uploadData.replaceExisting,
        runAiDetection: uploadData.runAiDetection,
      });

      // Step 2: Auto-approve the uploaded document(s)
      const { DocumentsApi, DocumentReviewAction } = await import('@ata-digital/client-tax-system-api-client');
      const documentsApi = new DocumentsApi(fileUploadConfig, undefined, apiAxiosInstance);

      // Type guards for upload response structure
      function isDocumentIdResponse(data: unknown): data is { documentId: string } {
        return typeof data === 'object' && data !== null && 'documentId' in data && typeof data.documentId === 'string';
      }

      function isDocumentsArrayResponse(data: unknown): data is { documents: Array<{ id: string }> } {
        return (
          typeof data === 'object' &&
          data !== null &&
          'documents' in data &&
          Array.isArray(data.documents) &&
          data.documents.length > 0 &&
          typeof data.documents[0]?.id === 'string'
        );
      }

      // Get the uploaded document ID from the response using type guards
      const responseData = uploadResponse.data;
      let uploadedDocumentId: string | undefined;
      if (isDocumentIdResponse(responseData)) {
        uploadedDocumentId = responseData.documentId;
      } else if (isDocumentsArrayResponse(responseData)) {
        uploadedDocumentId = responseData.documents[0].id;
      }

      if (uploadedDocumentId) {
        // Approve the uploaded document
        await documentsApi.apiDocumentsRequestDocumentRequestIdBulkReviewPost({
          documentRequestId: uploadData.documentRequestId,
          bulkReviewDocumentsDto: {
            documents: [
              {
                documentId: uploadedDocumentId,
                action: DocumentReviewAction.Approve,
                reviewNotes: 'Uploaded by preparer on behalf of client',
              },
            ],
          },
        });
      }

      return uploadResponse.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate organizer lists to refresh document request status
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      // Invalidate all organizer details since document requests are nested
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
      // Invalidate document request specific queries
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.detail(variables.documentRequestId) });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      // Invalidate document request documents to refresh the document list
      queryClient.invalidateQueries({ queryKey: ['documents', 'request', variables.documentRequestId] });
      // Also invalidate awaiting review documents
      queryClient.invalidateQueries({ queryKey: ['documentsAwaitingReview', variables.documentRequestId] });
      // Invalidate all document lists (including organizer document lists) to refresh DocumentsList
      queryClient.invalidateQueries({ queryKey: ['documents', 'list'] });
    },
    onError: (error) => {
      log.error('Document request upload and approve failed:', error);
    },
  });
}

// Hook for getting document requests awaiting review
export function useDocumentRequestsAwaitingReview(params?: {
  binderId?: string;
  clientId?: string;
  status?: DocumentRequestStatus;
  priority?: DocumentRequestPriority;
  page?: number;
  limit?: number;
}) {
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: documentRequestKeys.awaitingReviewList(params),
    queryFn: async () => {
      const response = await documentRequestsApi.apiDocumentRequestsAwaitingReviewGet({
        clientId: params?.clientId,
        binderId: params?.binderId,
        status: params?.status,
        priority: params?.priority,
        page: params?.page,
        pageSize: params?.limit,
      });
      return response.data?.data || [];
    },
    enabled: !!authState?.isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes - review queue changes frequently
  });
}

// TODO(#62): Review feature - commenting out until API client is updated
// Hook for reviewing a document request
export function useReviewDocumentRequest() {
  const queryClient = useQueryClient();
  // const { data: authState } = useAuthState();
  // const documentRequestsApi = new DocumentRequestsApi(
  //   createApiConfig(authState?.token || undefined),
  //   undefined,
  //   apiAxiosInstance
  // );

  return useMutation({
    mutationFn: async (reviewData: {
      documentRequestId: string;
      action: string; // DocumentRequestReviewAction;
      reviewNotes?: string;
      rejectionReason?: string;
    }) => {
      // TODO(#62): Implement when API types are available
      // const reviewDto: ReviewDocumentRequestDto = {
      //   action: reviewData.action,
      //   reviewNotes: reviewData.reviewNotes,
      //   rejectionReason: reviewData.rejectionReason,
      // };

      // const response = await documentRequestsApi.apiDocumentRequestsIdReviewPost({
      //   id: reviewData.documentRequestId,
      //   reviewDocumentRequestDto: reviewDto,
      // });
      // return response.data;

      log.debug('TODO(#62): Review document request', reviewData);
      return Promise.resolve({ success: true });
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.detail(variables.documentRequestId) });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.awaitingReview() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
    },
    onError: (error) => {
      log.error('Document request review failed:', error);
    },
  });
}

// Hook for getting a specific document request
export function useDocumentRequest(documentRequestId: string) {
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: documentRequestKeys.detail(documentRequestId),
    queryFn: async () => {
      const response = await documentRequestsApi.apiDocumentRequestsIdGet({
        id: documentRequestId,
      });
      return response.data?.data || null;
    },
    enabled: !!authState?.isAuthenticated && !!documentRequestId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Hook for listing document requests
export function useDocumentRequests(params?: {
  binderId?: string;
  clientId?: string;
  status?: DocumentRequestStatus;
  priority?: DocumentRequestPriority;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}) {
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: documentRequestKeys.list(params),
    queryFn: async () => {
      const response = await documentRequestsApi.apiDocumentRequestsGet({
        clientId: params?.clientId,
        binderId: params?.binderId,
        status: params?.status,
        priority: params?.priority,
        sortBy: params?.sortBy,
        sortDirection: params?.sortOrder,
        page: params?.page,
        pageSize: params?.limit,
        search: params?.search,
      });
      // Return the full paginated response with items and pagination
      return (
        response.data?.data || {
          items: [],
          pagination: {
            page: 1,
            pageSize: 10,
            totalCount: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        }
      );
    },
    enabled: !!authState?.isAuthenticated,
    staleTime: 1000 * 60 * 2, // 2 minutes - reduced since we have filters/pagination
  });
}

// Hook for creating a document request
export function useCreateDocumentRequest() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (data: CreateDocumentRequestDto) => {
      const response = await documentRequestsApi.apiDocumentRequestsPost({
        createDocumentRequestDto: data,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
    },
    onError: (error) => {
      log.error('Document request creation failed:', error);
    },
  });
}

// Hook for updating a document request
export function useUpdateDocumentRequest() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (updateData: { documentRequestId: string; data: UpdateDocumentRequestDto }) => {
      const response = await documentRequestsApi.apiDocumentRequestsIdPut({
        id: updateData.documentRequestId,
        updateDocumentRequestDto: updateData.data,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.detail(variables.documentRequestId) });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.awaitingReview() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
    },
    onError: (error) => {
      log.error('Document request update failed:', error);
    },
  });
}

// Hook for deleting a document request
export function useDeleteDocumentRequest() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (documentRequestId: string) => {
      const response = await documentRequestsApi.apiDocumentRequestsIdDelete({
        id: documentRequestId,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.awaitingReview() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
    },
    onError: (error) => {
      log.error('Document request deletion failed:', error);
    },
  });
}

// Comment-related hooks - using centralized query options and keys from @/lib/query/options
// Re-export commentKeys for backward compatibility
export { commentKeys, type CommentFilters } from '@/lib/query/options';

/**
 * Hook for getting comments for a document request
 * Uses centralized query options from src/lib/query/options.ts
 */
export function useDocumentRequestComments(params: {
  documentRequestId?: string;
  binderId?: string;
  page?: number;
  limit?: number;
}) {
  const { data: authState } = useAuthState();

  // Use the appropriate query option based on what's provided
  const queryOption = params.documentRequestId
    ? commentQueryOptions.byDocumentRequest(
        params.documentRequestId,
        {
          page: params.page,
          limit: params.limit,
        },
        authState?.token || undefined
      )
    : params.binderId
      ? commentQueryOptions.byBinder(
          params.binderId,
          {
            page: params.page,
            limit: params.limit,
          },
          authState?.token || undefined
        )
      : commentQueryOptions.list(
          {
            page: params.page,
            limit: params.limit,
          },
          authState?.token || undefined
        );

  return useQuery({
    ...queryOption,
    enabled: !!authState?.isAuthenticated && (!!params.documentRequestId || !!params.binderId),
  });
}

// Hook for creating a comment
export function useCreateComment() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const commentsApi = new CommentsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (commentData: CreateCommentDto) => {
      const response = await commentsApi.apiCommentsPost({
        createCommentDto: commentData,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate comment lists to refresh data
      queryClient.invalidateQueries({ queryKey: commentKeys.lists() });
      if (variables.binderId) {
        queryClient.invalidateQueries({ queryKey: commentKeys.list({ binderId: variables.binderId }) });
      }
      if (variables.documentRequestId) {
        queryClient.invalidateQueries({
          queryKey: commentKeys.list({ documentRequestId: variables.documentRequestId }),
        });
      }
      // Also invalidate document request data to update comment count
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.details() });
    },
    onError: (error) => {
      log.error('Comment creation failed:', error);
    },
  });
}

// Hook for marking a comment as read
export function useMarkCommentAsRead() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const commentsApi = new CommentsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (commentId: string) => {
      const response = await commentsApi.apiCommentsIdReadPut({
        id: commentId,
      });
      return response.data?.data || null;
    },
    onSuccess: () => {
      // Invalidate all comment queries to refresh the read status
      queryClient.invalidateQueries({ queryKey: commentKeys.all });
    },
    onError: (error) => {
      log.error('Failed to mark comment as read:', error);
    },
  });
}

// Hook for updating a comment
export function useUpdateComment() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const commentsApi = new CommentsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (updateData: { commentId: string; data: UpdateCommentDto }) => {
      const response = await commentsApi.apiCommentsIdPut({
        id: updateData.commentId,
        updateCommentDto: updateData.data,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate comment lists to refresh data
      queryClient.invalidateQueries({ queryKey: commentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: commentKeys.details() });
    },
    onError: (error) => {
      log.error('Comment update failed:', error);
    },
  });
}

// Hook for deleting a comment
export function useDeleteComment() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const commentsApi = new CommentsApi(createApiConfig(authState?.token || undefined), undefined, apiAxiosInstance);

  return useMutation({
    mutationFn: async (commentId: string) => {
      const response = await commentsApi.apiCommentsIdDelete({ id: commentId });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate comment lists to refresh data
      queryClient.invalidateQueries({ queryKey: commentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: commentKeys.details() });
      // Update document request comment counts
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.details() });
    },
    onError: (error) => {
      log.error('Comment deletion failed:', error);
    },
  });
}

// Type for audit filters
type AuditFilters = {
  entityType?: string;
  entityId?: string;
  userId?: string;
  action?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
};

// Audit/History hooks
export const auditKeys = {
  all: ['audit'] as const,
  lists: () => [...auditKeys.all, 'list'] as const,
  list: (filters?: AuditFilters) => [...auditKeys.lists(), filters] as const,
  entity: (entityType: string, entityId: string) => [...auditKeys.all, 'entity', entityType, entityId] as const,
} as const;

// Hook for getting audit events for a document request (DEPRECATED - use useDocumentRequestAuditHistory instead)
// This hook is commented out because AuditEventsApi is no longer available in the API client
// Use useDocumentRequestAuditHistory for document request audit history
/*
export function useDocumentRequestHistory(documentRequestId: string) {
  const { data: authState } = useAuthState();
  const auditEventsApi = new AuditEventsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: auditKeys.entity('DocumentRequest', documentRequestId),
    queryFn: async () => {
      const response = await auditEventsApi.apiAuditEventsEntityEntityTypeEntityIdGet({
        entityType: 'DocumentRequest',
        entityId: documentRequestId,
      });
      return response.data?.data || [];
    },
    enabled: !!authState?.isAuthenticated && !!documentRequestId,
    staleTime: 1000 * 60 * 5, // 5 minutes - history doesn't change often
  });
}
*/

// Hook for getting human-readable audit history for a document request
export function useDocumentRequestAuditHistory(documentRequestId: string) {
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: [...documentRequestKeys.detail(documentRequestId), 'auditHistory'] as const,
    queryFn: async () => {
      const response = await documentRequestsApi.apiDocumentRequestsIdAuditHistoryGet({
        id: documentRequestId,
      });
      return response.data?.data || null;
    },
    enabled: !!authState?.isAuthenticated && !!documentRequestId,
    staleTime: 1000 * 60 * 5, // 5 minutes - history doesn't change often
  });
}

// Hook for getting general audit events with filters (DEPRECATED)
// This hook is commented out because AuditEventsApi is no longer available in the API client
// Use entity-specific audit history hooks instead (useDocumentRequestAuditHistory, useTaxBinderAuditHistory, etc.)
/*
export function useAuditEvents(params?: {
  entityType?: string;
  entityId?: string;
  userId?: string;
  action?: string;
  fromDate?: string;
  toDate?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
}) {
  const { data: authState } = useAuthState();
  const auditEventsApi = new AuditEventsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useQuery({
    queryKey: auditKeys.list(params),
    queryFn: async () => {
      const response = await auditEventsApi.apiAuditEventsGet(params || {});
      return response.data?.data || { items: [], pagination: null };
    },
    enabled: !!authState?.isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
*/

// Hook for client to respond to a document request
export function useClientResponseToDocumentRequest() {
  const queryClient = useQueryClient();
  const { data: authState } = useAuthState();
  const documentRequestsApi = new DocumentRequestsApi(
    createApiConfig(authState?.token || undefined),
    undefined,
    apiAxiosInstance
  );

  return useMutation({
    mutationFn: async (responseData: { documentRequestId: string; responseDto: ClientResponseDto }) => {
      const response = await documentRequestsApi.apiDocumentRequestsIdClientResponsePost({
        id: responseData.documentRequestId,
        clientResponseDto: responseData.responseDto,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.detail(variables.documentRequestId) });
      queryClient.invalidateQueries({ queryKey: documentRequestKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: taxBinderKeys.details() });
      // Invalidate comments as the response creates a comment
      queryClient.invalidateQueries({ queryKey: commentKeys.lists() });
    },
    onError: (error) => {
      log.error('Document request response failed:', error);
    },
  });
}

// Re-export types and enum for convenience
export type { ClientResponseDto };
export { ClientResponseType };
